import React, { useMemo } from 'react';
import { Shield, Users, LogOut, Edit3, Trash2, MoreVertical, ShieldAlert } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';

const TeamDetailModal = ({ team, onClose, onLeaveTeam, onKickMember, onPromoteMember, onDeleteTeam, onEditTeam }) => {
    const { user: currentUser } = useUser();

    // Sort roster: Captain first, then members
    const sortedRoster = useMemo(() => {
        if (!team.roster) return [];
        return Object.entries(team.roster)
            .map(([uid, data]) => ({ uid, ...data }))
            .sort((a, b) => (a.role === 'captain' ? -1 : 1));
    }, [team.roster]);

    const isCaptain = team.createdBy === currentUser?.uid;
    const isMember = team.roster && team.roster[currentUser?.uid];

    // Helper to get initials
    const getInitials = (name) => name ? name.substring(0, 2).toUpperCase() : '??';

    if (!team) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-md bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div
                    className="h-32 relative flex items-end p-6"
                    style={{ background: `linear-gradient(135deg, ${team.meta?.theme?.primary || '#333'}, ${team.meta?.theme?.accent || '#111'})` }}
                >
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-white transition backdrop-blur-md"
                    >
                        <MoreVertical size={20} className="hidden" /> {/* Placeholder/Menu trigger if needed */}
                        <span className="text-2xl">&times;</span>
                    </button>

                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 flex items-center justify-center text-3xl font-black text-white shadow-lg">
                            {team.meta?.name?.charAt(0)}
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white leading-tight">{team.meta?.name}</h2>
                            <p className="text-white/70 text-sm flex items-center gap-1.5">
                                <Users size={12} />
                                {Object.keys(team.roster || {}).length} Members
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-900">
                    {/* Description */}
                    {team.meta?.description && (
                        <div className="mb-6 p-4 bg-gray-800/50 rounded-xl border border-gray-800">
                            <p className="text-gray-400 italic text-sm">"{team.meta.description}"</p>
                        </div>
                    )}

                    {/* Roster Section */}
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Team Roster</h3>
                    <div className="space-y-2">
                        {sortedRoster.map((member) => {
                            // In a real app, you'd fetch user details (name, avatar) for each ID.
                            // For MVP, we might only have ID. 
                            // *Improvement*: Store displayName in roster or fetch it.
                            // Assuming for now we might create a mechanism to store names in roster on join.
                            // If name missing, fallback to "Player".
                            const isMe = member.uid === currentUser?.uid;

                            return (
                                <div key={member.uid} className="flex items-center gap-3 p-3 bg-gray-800 rounded-xl border border-gray-700">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${member.role === 'captain' ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-300'}`}>
                                        {member.role === 'captain' ? 'C' : 'M'}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-white">
                                            {isMe ? 'You' : (member.displayName || 'Team Member')}
                                            {member.role === 'captain' && <span className="ml-2 text-[10px] bg-yellow-500/20 text-yellow-500 px-1.5 py-0.5 rounded uppercase">Captain</span>}
                                        </p>
                                    </div>

                                    {/* Admin Actions (Only for Captain affecting others) */}
                                    {isCaptain && !isMe && (
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => onKickMember(member.uid)}
                                                className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg transition"
                                                title="Kick Member"
                                            >
                                                <LogOut size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-4 bg-gray-800 border-t border-gray-700 flex flex-col gap-2">
                    {/* Member Action: Leave */}
                    {isMember && !isCaptain && (
                        <button
                            onClick={() => onLeaveTeam(team.id)}
                            className="w-full py-3 bg-red-500/10 text-red-500 font-bold rounded-xl hover:bg-red-500/20 transition flex items-center justify-center gap-2 border border-red-500/20"
                        >
                            <LogOut size={18} /> Leave Team
                        </button>
                    )}

                    {/* Captain Actions */}
                    {isCaptain && (
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => onEditTeam(team)}
                                className="py-3 bg-gray-700 text-white font-bold rounded-xl hover:bg-gray-600 transition flex items-center justify-center gap-2"
                            >
                                <Edit3 size={18} /> Edit
                            </button>
                            <button
                                onClick={() => onDeleteTeam(team.id)}
                                className="py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition flex items-center justify-center gap-2"
                            >
                                <Trash2 size={18} /> Disband
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamDetailModal;
